import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Users,
  TrendingUp,
  Eye,
  DollarSign,
  CheckCircle,
  Globe,
  Shield,
  ExternalLink,
  ArrowLeft,
} from "lucide-react";
import Footer from "../../components/landing/Footer";
import Config from "../../utils/Config";
import InteractiveSteps from "../../components/misc/InteractiveSteps";

const BecomeAProvider = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    window.history.back();
  };

  const handlePortalRedirect = () => {
    window.open(Config.PROVIDER_PORTAL_URL, "_blank");
  };

  const benefits = [
    {
      icon: TrendingUp,
      title: "Expand Your Reach",
      description: "Access thousands of travelers seeking safe, verified transport and accommodation services across Ghana.",
      color: "bg-emerald-500"
    },
    {
      icon: Eye,
      title: "Verified & Trusted",
      description: "Build trust with customers through TripSecure's verification system and safety standards.",
      color: "bg-blue-500"
    },
    {
      icon: DollarSign,
      title: "Increase Revenue",
      description: "Boost your bookings and revenue through our platform's marketing and customer acquisition.",
      color: "bg-purple-500"
    },
    {
      icon: Shield,
      title: "Risk Protection",
      description: "Reduce liability and risk with TripSecure's comprehensive insurance and safety coverage.",
      color: "bg-orange-500"
    }
  ];

  const requirements = [
    "Valid business registration and operating license",
    "Comprehensive insurance coverage",
    "Well-maintained vehicles with safety features",
    "Trained and certified drivers",
    "Clean safety and service record",
    "Commitment to TripSecure's safety standards"
  ];

  const processSteps = [
    {
      title: "Apply Online",
      description: "Submit your business details and documentation through our secure online application portal",
      icon: Users,
      details: [
        "Complete the online application form",
        "Upload business registration documents",
        "Provide insurance and safety certificates",
        "Submit driver and vehicle information"
      ]
    },
    {
      title: "Verification Process",
      description: "Our team reviews your application and conducts thorough background checks and verification",
      icon: Eye,
      details: [
        "Document verification and review",
        "Background checks on drivers",
        "Vehicle inspection and certification",
        "Safety standards compliance check"
      ]
    },
    {
      title: "Onboarding & Training",
      description: "Complete our safety training program and integrate with our platform systems",
      icon: Shield,
      details: [
        "Complete safety training modules",
        "Platform integration and setup",
        "Learn TripSecure safety protocols",
        "Access partner dashboard and tools"
      ]
    },
    {
      title: "Go Live",
      description: "Start receiving bookings and serving TripSecure customers with verified safety standards",
      icon: TrendingUp,
      details: [
        "Profile goes live on platform",
        "Start receiving customer bookings",
        "Access real-time booking management",
        "Begin earning from verified services"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50">
      {/* Back Button */}
      <div className="fixed top-24 left-4 z-50">
        <motion.button
          onClick={handleBack}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg hover:bg-white hover:shadow-xl transition-all duration-300"
        >
          <ArrowLeft className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Back</span>
        </motion.button>
      </div>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/10 to-blue-600/10" />
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-3xl bg-emerald-300/20" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full blur-3xl bg-blue-300/20" />
        
        <div className="relative z-10 px-4 mx-auto max-w-6xl sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 border border-emerald-200 mb-6">
              <Users className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Partner Network</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl lg:text-6xl mb-6">
              Join the TripSecure
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-blue-600">
                Partner Network
              </span>
            </h1>
            
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Partner with Africa's leading travel safety platform and grow your business while providing secure, verified services to thousands of travelers.
            </p>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-gray-200 max-w-2xl mx-auto">
              <p className="text-sm text-gray-600">
                At TripSecure, we empower businesses to grow through our Pay-As-You-Go (PAYG) Travel Insurance, Tours & Smart Logistics, Safety Services, and Media & Events divisions.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 mx-auto max-w-6xl sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Partner with TripSecure?</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Join a network of trusted providers committed to safety, quality, and customer satisfaction.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5, scale: 1.02 }}
              className="group relative p-6 rounded-2xl bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
            >
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${benefit.color} mb-4`}>
                <benefit.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
              <p className="text-sm text-gray-600">{benefit.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Requirements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Partnership Requirements</h2>
            <p className="text-lg text-gray-600">
              To join our partner network, your business must meet these standards:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {requirements.map((requirement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200"
              >
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-800">{requirement}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Interactive Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <InteractiveSteps
            title="How It Works"
            subtitle="Join our partner network in just four simple steps and start growing your business with TripSecure"
            steps={processSteps}
            theme="emerald"
            autoPlay={true}
            autoPlayInterval={4000}
          />
        </motion.div>

        {/* Portal Access */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Start Your Partnership Journey</h2>
            <p className="text-lg text-gray-600 mb-6">
              Ready to join our network? Access our dedicated provider portal to begin your application process.
            </p>
            <p className="text-sm text-gray-500">
              Our portal provides a streamlined application process with real-time updates on your partnership status.
            </p>
          </div>

          <div className="flex flex-col items-center space-y-6">
            <motion.button
              onClick={handlePortalRedirect}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="group flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-emerald-600 to-blue-600 text-white font-semibold rounded-xl hover:from-emerald-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <Globe className="w-5 h-5" />
              <span>Access Provider Portal</span>
              <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </motion.button>

            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Provider Portal:</strong> {Config.PROVIDER_PORTAL_URL.replace('https://', '')}
              </p>
              <p className="text-xs text-gray-500">
                Click the button above to open the portal in a new tab
              </p>
            </div>
          </div>
        </motion.div>

        {/* Final Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Grow Your Business?</h3>
            <p className="text-lg text-gray-600 mb-6">
              Join hundreds of successful partners who are already growing their business with TripSecure. 
              Your journey to increased revenue and customer trust starts here.
            </p>
            <div className="flex items-center justify-center gap-2 text-emerald-600 font-semibold">
              <TrendingUp className="w-5 h-5" />
              <span>Start Your Partnership Today</span>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default BecomeAProvider;
